import {
  ACTIVE_PROJECT_STATUSES,
  ACTIVITY_WINDOW_DAYS,
  QUIET_CUSTOMER_DAYS,
} from '@brickup/shared/constants'
import type { SalesDashboardResponse } from '@brickup/shared/dto'
import { ProjectStatus } from '@brickup/shared/enums'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { NAV } from '@brickup/shared/locales/de/nav'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { TERMS } from '@brickup/shared/locales/de/terms'
import { formatDateTime } from '@brickup/shared/utils'
import { ActiveProjectsPanel } from './ActiveProjectsPanel'
import { ActivityPanel } from './ActivityPanel'
import { moreFooter, projectLink, projectsLink } from './dashboard-links'
import { DashboardData, DashboardPanel, DashboardRow } from './DashboardLayout'
import { GLANCE } from '@/api/glance'
import { useRobotStatus } from '@/api/matching'
import { useSalesDashboard } from '@/api/staff-dashboard'
import { Callout, dash, TONE_INK } from '@/components/base'
import { LabelValue } from '@/components/Internal/LabelValue'
import { LabelValueGroup } from '@/components/Internal/LabelValueGroup'
import { LinkRowList } from '@/components/Internal/LinkRow'
import { LinkButton } from '@/components/LinkButton'
import { COUNTED_PROJECT_STATUSES } from '@/constants/project-status-options'
import { useIsProjectScoped } from '@/hooks/use-project-access'
import { metaLine } from '@/utils/meta-line'

const COPY = STAFF_DASHBOARD.sales

/**
 * What the reader's customers did, which of their projects have gone quiet,
 * and where each project stands.
 */
export function SalesDashboard() {
  const query = useSalesDashboard()
  return (
    <DashboardData query={query}>{data => <Sales data={data} />}</DashboardData>
  )
}

function Sales({ data }: { data: SalesDashboardResponse }) {
  const { data: robot } = useRobotStatus(GLANCE)
  // The figures count the reader's own projects, so the lists they lead to
  // open on the same set.
  const scoped = useIsProjectScoped()

  const count = (status: ProjectStatus) => data.statusCounts[status] ?? 0
  const total = COUNTED_PROJECT_STATUSES.reduce(
    (sum, status) => sum + count(status),
    0,
  )
  const active = ACTIVE_PROJECT_STATUSES.reduce(
    (sum, status) => sum + count(status),
    0,
  )
  const quiet = data.quietCount

  return (
    <>
      <LabelValueGroup variant="tiles">
        <LabelValue
          label={STAFF_DASHBOARD.activeProjects}
          value={active}
          hint={COPY.activeProjectsHint(total)}
          to={projectsLink({ mine: scoped })}
        />
        <LabelValue
          label={STAFF_DASHBOARD.activity}
          value={
            data.activityCapped
              ? COPY.atLeast(data.activityCount)
              : data.activityCount
          }
          hint={STAFF_DASHBOARD.lastDays(ACTIVITY_WINDOW_DAYS)}
        />
        <LabelValue
          label={COPY.quiet}
          value={quiet}
          valueClassName={quiet > 0 ? TONE_INK.warning : undefined}
          hint={COPY.quietHint(QUIET_CUSTOMER_DAYS)}
        />
        {data.matching ? (
          <LabelValue
            label={COPY.unacknowledged}
            value={data.matching.unacknowledgedReactions}
            valueClassName={
              data.matching.unacknowledgedReactions > 0
                ? TONE_INK.warning
                : undefined
            }
            hint={COPY.unacknowledgedHint}
            to={{ to: '/internal/matching' }}
          />
        ) : null}
      </LabelValueGroup>

      <DashboardRow columns="wide-narrow">
        <ActivityPanel
          subtitle={COPY.feedTitle}
          activity={data.activity}
          activityCount={data.activityCount}
        />

        <div className="flex min-w-0 flex-col gap-4">
          <DashboardPanel
            title={COPY.quiet}
            subtitle={COPY.quietHint(QUIET_CUSTOMER_DAYS)}
            footer={moreFooter(quiet, data.quietProjects.length)}
          >
            <LinkRowList
              rows={data.quietProjects.map(project => ({
                key: project.id,
                title: project.name,
                subtitle: metaLine(
                  project.customerName ?? TERMS.customer,
                  project.lastCustomerActivityAt
                    ? STAFF_DASHBOARD.lastActive(
                        formatDateTime(project.lastCustomerActivityAt),
                      )
                    : COPY.neverActive,
                ),
                to: projectLink(project.id),
              }))}
              emptyTitle={COPY.noQuiet}
            />
          </DashboardPanel>

          <DashboardPanel
            title={NAV.matching}
            action={
              <LinkButton to="/internal/matching" variant="link" size="sm">
                {COPY.openMatching}
              </LinkButton>
            }
          >
            {data.matching ? (
              <LabelValueGroup>
                <LabelValue
                  label={COPY.toReview}
                  value={data.matching.toReview}
                />
                <LabelValue
                  label={CONTROLS.cronJob.nextRun}
                  value={robot?.nextRun ? formatDateTime(robot.nextRun) : dash}
                />
              </LabelValueGroup>
            ) : (
              <Callout tone="warning">{COPY.matchingUnavailable}</Callout>
            )}
          </DashboardPanel>
        </div>
      </DashboardRow>

      <ActiveProjectsPanel
        statusCounts={data.statusCounts}
        projects={data.projects}
      />
    </>
  )
}
